"use client";
import { useState } from 'react';
import { useTransition, animated } from '@react-spring/web';
import { X, Download, FileText, Loader2 } from 'lucide-react';
import { GOOGLE_SCRIPT_URL } from '@/lib/config';
import { trackDossierDownload } from '@/components/PixelTracker';

interface DossierModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DossierModal({ isOpen, onClose }: DossierModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for phone field - only allow + and numbers
    if (name === 'phone') {
      const cleanedValue = value.replace(/[^+\d]/g, '');
      setFormData(prev => ({
        ...prev,
        phone: cleanedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El correo electrónico no es válido';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es obligatorio';
    } else if (!/^\+?\d+$/.test(formData.phone)) {
      newErrors.phone = 'El teléfono debe contener solo números y puede empezar con +';
    }
    
    if (!formData.consent) {
      newErrors.consent = 'Debes aceptar la política de privacidad';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Send data to Google Sheets
      const url = GOOGLE_SCRIPT_URL;
      
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: `Nombre=${encodeURIComponent(formData.name)}&Email=${encodeURIComponent(formData.email)}&Phone=${encodeURIComponent(formData.phone)}&Consentimiento=${formData.consent ? 'true' : 'false'}`
      });
      
      const data = await response.text();
      console.log('Google Sheets response:', data);
      
      // Download the dossier
      const link = document.createElement('a');
      link.href = '/Dosier-Informativo-Certificacion-Coaching.pdf';
      link.download = 'Dosier-Certificacion-Coaching-X-Valores.pdf';
      link.click();
      
      // Track dossier download
      trackDossierDownload();
      
      // Reset form and close modal
      setFormData({ name: '', email: '', phone: '', consent: false });
      onClose();
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Hubo un error al enviar los datos. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const transitions = useTransition(isOpen, {
    from: { opacity: 0, transform: 'scale(0.95)' },
    enter: { opacity: 1, transform: 'scale(1)' },
    leave: { opacity: 0, transform: 'scale(0.95)' },
    config: { duration: 300 }
  });

  return (
    <>
      {transitions((style, item) =>
        item ? (
          <>
            {/* Backdrop */}
            <animated.div
              style={{ opacity: style.opacity }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            
            {/* Modal - Full Screen */}
            <animated.div
              style={style}
              className="fixed inset-0 bg-transparent z-50 overflow-hidden overflow-y-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="dossier-modal-title"
            >
              <div className="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-md mx-auto">
                  {/* Header */}
                  <div className="relative bg-gradient-to-r from-[#97d700] to-[#7fb500] p-4 sm:p-6 text-center rounded-t-3xl">
                    <button
                      onClick={onClose}
                      className="absolute right-4 top-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                      aria-label="Cerrar formulario de descarga del dossier"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                    
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-[#97d700]" />
                    </div>
                    
                    <h2 id="dossier-modal-title" className="text-xl sm:text-2xl font-heading uppercase tracking-wider text-white mb-2">
                      Descarga el Dossier Informativo
                    </h2>
                    <p className="text-sm sm:text-base text-white/90">
                      Solo tienes que rellenar el formulario
                    </p>
                  </div>
                  
                  {/* Form */}
                  <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 space-y-4 rounded-b-3xl shadow-xl">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Tu nombre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                        placeholder="Juan García"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>
                    
                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Tu correo electrónico <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                        placeholder="juan@ejemplo.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                    
                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Tu teléfono (con prefijo) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        } focus:outline-none focus:ring-2 focus:ring-[#97d700] focus:border-transparent transition-all`}
                        placeholder="+34600123456"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                    
                    {/* Consent */}
                    <div>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={formData.consent}
                          onChange={handleInputChange}
                          className="mt-1 w-4 h-4 rounded border-gray-300 text-[#97d700] focus:ring-[#97d700]"
                          aria-describedby="consent-error"
                          aria-required="true"
                        />
                        <span className="text-sm text-gray-600">
                          He leído y acepto la{' '}
                          <a 
                            href="/politica-privacidad" 
                            target="_blank" 
                            className="text-[#97d700] hover:underline"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Leer política de privacidad (se abre en nueva pestaña)"
                          >
                            política de privacidad
                          </a>
                          . <span className="text-red-500">*</span>
                        </span>
                      </label>
                      {errors.consent && (
                        <p id="consent-error" className="text-red-500 text-sm mt-1">{errors.consent}</p>
                      )}
                    </div>
                    
                    {/* Privacy Notice */}
                    <div className="bg-gray-50 p-4 rounded-xl text-xs text-gray-600">
                      <p>
                        Certificación Coaching X Valores te informa que los datos de carácter personal 
                        que nos proporciones serán tratados por nosotros como responsable de esta web. 
                        La finalidad es para enviarte el dossier informativo, así como información sobre 
                        la certificación. Tu legitimación se realiza a través del consentimiento. 
                        Podrás ejercer tus derechos de acceso, rectificación, limitación y suprimir 
                        los datos en contacto@tonogalan.com
                      </p>
                    </div>
                    
                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-4 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold rounded-full shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                      aria-label={isSubmitting ? "Procesando..." : "Descargar dossier"}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Procesando...</span>
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5" />
                          <span>Descargar Dossier</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </animated.div>
          </>
        ) : null
      )}
    </>
  );
}